'use client';

import React, { useMemo } from 'react';
import { cx } from '../utils/cx';

export interface LineChartProps {
  /**
   * Values:
   * - Prefer number[] for portability
   * - Legacy CSV string supported
   */
  data: number[] | string;

  labels?: string[] | string;
  color?: string;
  lineWidth?: number;
  curved?: boolean;
  filled?: boolean;
  fillOpacity?: number;
  showDots?: boolean;
  showGrid?: boolean;
  showLabels?: boolean;
  animate?: boolean;
  height?: number;
  ariaLabel?: string;
  className?: string;
}

function parseNumbers(input: number[] | string): number[] {
  if (Array.isArray(input)) return input.filter((n) => typeof n === 'number' && Number.isFinite(n));
  return String(input ?? '')
    .split(',')
    .map((s) => Number.parseFloat(s.trim()))
    .filter((n) => Number.isFinite(n));
}

function parseStrings(input: string[] | string | undefined): string[] {
  if (!input) return [];
  if (Array.isArray(input)) return input.map((s) => String(s).trim()).filter(Boolean);
  return String(input)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function isCssColorValue(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return (
    v.startsWith('#') ||
    v.startsWith('rgb(') ||
    v.startsWith('rgba(') ||
    v.startsWith('hsl(') ||
    v.startsWith('hsla(') ||
    v.startsWith('var(')
  );
}

function resolveColorToken(token: string | undefined): string {
  const t = String(token ?? 'primary').trim();
  if (!t) return 'currentColor';
  if (isCssColorValue(t)) return t;
  return `var(--color-${t}, currentColor)`;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/**
 * Simple smoothing for "curved" sparklines.
 * Converts points to a quadratic-bezier-ish path.
 */
function buildSmoothPath(points: Array<{ x: number; y: number }>): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0]!.x} ${points[0]!.y}`;

  const d: string[] = [];
  d.push(`M ${points[0]!.x} ${points[0]!.y}`);

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i]!;
    const p1 = points[i + 1]!;
    const mx = (p0.x + p1.x) / 2;
    const my = (p0.y + p1.y) / 2;
    d.push(`Q ${p0.x} ${p0.y} ${mx} ${my}`);
  }

  const last = points[points.length - 1]!;
  d.push(`T ${last.x} ${last.y}`);
  return d.join(' ');
}

function buildLinearPath(points: Array<{ x: number; y: number }>): string {
  if (points.length === 0) return '';
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
}

export function LineChart({
  data,
  labels,
  color = 'primary',
  lineWidth = 2,
  curved = false,
  filled = false,
  fillOpacity = 0.15,
  showDots = false,
  showGrid = true,
  showLabels = true,
  animate = true,
  height = 120,
  ariaLabel,
  className,
}: LineChartProps) {
  const values = useMemo(() => parseNumbers(data), [data]);
  const labelList = useMemo(() => parseStrings(labels), [labels]);
  const stroke = useMemo(() => resolveColorToken(color), [color]);

  const model = useMemo(() => {
    const n = values.length;
    if (n < 2) return null;

    const paddingX = 10;
    const paddingTop = 10;
    const labelArea = showLabels && labelList.length > 0 ? 20 : 8;

    const w = 240; // viewBox width
    const h = 100; // viewBox height
    const plotH = h - paddingTop - labelArea;
    const plotW = w - paddingX * 2;

    const minV = Math.min(...values);
    const maxV = Math.max(...values);
    const span = maxV - minV || 1;

    const pts = values.map((v, i) => {
      const x = paddingX + (i / (n - 1)) * plotW;
      const yNorm = (v - minV) / span;
      const y = paddingTop + (1 - yNorm) * plotH;
      return { x, y };
    });

    const path = curved ? buildSmoothPath(pts) : buildLinearPath(pts);

    const areaPath = filled
      ? `${path} L ${paddingX + plotW} ${paddingTop + plotH} L ${paddingX} ${paddingTop + plotH} Z`
      : '';

    const grid = showGrid
      ? [0.25, 0.5, 0.75].map((p) => {
          const y = paddingTop + plotH * (1 - p);
          return (
            <line
              key={`g-${p}`}
              x1={paddingX}
              x2={paddingX + plotW}
              y1={y}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.12}
              strokeWidth={1}
            />
          );
        })
      : null;

    const xLabels =
      showLabels && labelList.length > 0
        ? labelList.slice(0, n).map((txt, i) => {
            if (!txt) return null;
            // avoid crowding: show at most 7 labels
            const step = Math.ceil(n / 7);
            if (n > 7 && i % step !== 0 && i !== n - 1) return null;

            const x = pts[i]!.x;
            const y = paddingTop + plotH + 16;
            return (
              <text
                key={`lbl-${i}`}
                x={x}
                y={y}
                textAnchor="middle"
                fontSize="10"
                fill="currentColor"
                opacity={0.55}
              >
                {txt}
              </text>
            );
          })
        : null;

    return { w, h, pts, path, areaPath, grid, xLabels, paddingX, paddingTop, plotH, plotW };
  }, [curved, filled, labelList, showGrid, showLabels, values]);

  if (!model) return null;

  const safeLineWidth = Number.isFinite(lineWidth) ? clamp(lineWidth, 1, 8) : 2;
  const safeFillOpacity = Number.isFinite(fillOpacity) ? clamp(fillOpacity, 0, 1) : 0.15;

  const a11yProps = ariaLabel
    ? ({ role: 'img', 'aria-label': ariaLabel } as const)
    : ({ role: 'presentation', 'aria-hidden': true } as const);

  const dashStyle = animate
    ? ({
        strokeDasharray: 400,
        strokeDashoffset: 400,
        animation: 'line-draw 700ms ease-out forwards',
      } as React.CSSProperties)
    : undefined;

  return (
    <svg
      {...a11yProps}
      className={cx('w-full text-tertiary', className)}
      viewBox={`0 0 ${model.w} ${model.h}`}
      height={height}
      preserveAspectRatio="xMidYMid meet"
    >
      {ariaLabel ? <title>{ariaLabel}</title> : null}

      {/* Minimal inline keyframes to avoid external CSS dependencies */}
      {animate ? (
        <style>
          {`@keyframes line-draw { to { stroke-dashoffset: 0; } } @media (prefers-reduced-motion: reduce){ path[data-animate="true"]{ animation:none !important; stroke-dasharray:none !important; stroke-dashoffset:0 !important; } }`}
        </style>
      ) : null}

      {model.grid}

      {filled ? <path d={model.areaPath} fill={stroke} opacity={safeFillOpacity} /> : null}

      <path
        d={model.path}
        fill="none"
        stroke={stroke}
        strokeWidth={safeLineWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        data-animate={animate ? 'true' : undefined}
        style={dashStyle}
      />

      {showDots ? model.pts.map((p, i) => <circle key={`d-${i}`} cx={p.x} cy={p.y} r={2.5} fill={stroke} opacity={0.9} />) : null}

      {model.xLabels}
    </svg>
  );
}

LineChart.displayName = 'LineChart';
